import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { PublicDocument, PublicDocumentTag } from "@vedaunion/shared";
import { useDocuments, useDocumentTags } from "../../../lib/documents";
import { LibraryCard } from "../../../components/document/LibraryCard";

/**
 * Library → Documents. Viewer-facing browse: published-only, tag-filtered,
 * search box.
 *
 * Filters: a "type" dropdown and a "deity" dropdown. Both may be active at
 * once — the server AND-intersects the resulting tag list, so e.g. selecting
 * `type:mantra` and `deity:shiva` returns mantras to Śiva specifically. A
 * "Clear filters" affordance reveals once anything is applied.
 *
 * The reading destination is `/library/:slug` (the public detail page) —
 * same URL whether you arrive from the public landing or the dashboard.
 */

export default function LibraryDocuments() {
  const [params, setParams] = useSearchParams();
  const tagParam = params.get("tag") ?? "";
  const q = params.get("q") ?? "";

  const tagsQ = useDocumentTags();
  const allTags = tagsQ.data?.tags ?? [];
  const typeTags = useMemo(
    () =>
      allTags
        .filter((t) => t.namespace === "type")
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    [allTags],
  );
  const deityTags = useMemo(
    () =>
      allTags
        .filter((t) => t.namespace === "deity")
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    [allTags],
  );

  // Parse the comma-separated tag param into per-namespace selections so the
  // dropdowns can read their current value without recomputing the join.
  const { selectedType, selectedDeity, otherTags } = useMemo(() => {
    let selectedType = "";
    let selectedDeity = "";
    const otherTags: string[] = [];
    if (tagParam) {
      for (const spec of tagParam.split(",").map((s) => s.trim()).filter(Boolean)) {
        if (spec.startsWith("type:")) selectedType = spec.slice(5);
        else if (spec.startsWith("deity:")) selectedDeity = spec.slice(6);
        else otherTags.push(spec);
      }
    }
    return { selectedType, selectedDeity, otherTags };
  }, [tagParam]);

  const setTagParam = (next: { type?: string; deity?: string }) => {
    const updatedType = next.type !== undefined ? next.type : selectedType;
    const updatedDeity = next.deity !== undefined ? next.deity : selectedDeity;
    const parts: string[] = [];
    if (updatedType) parts.push(`type:${updatedType}`);
    if (updatedDeity) parts.push(`deity:${updatedDeity}`);
    parts.push(...otherTags);
    const sp = new URLSearchParams(params);
    if (parts.length === 0) sp.delete("tag");
    else sp.set("tag", parts.join(","));
    setParams(sp, { replace: true });
  };

  const setSearch = (value: string | null) => {
    const sp = new URLSearchParams(params);
    if (value === null || value === "") sp.delete("q");
    else sp.set("q", value);
    setParams(sp, { replace: true });
  };

  const docsQ = useDocuments({
    limit: 100,
    ...(tagParam ? { tag: tagParam } : {}),
    ...(q ? { q } : {}),
  });
  const documents = docsQ.data?.documents ?? [];
  const hasFilters = !!(selectedType || selectedDeity || q);

  return (
    <div className="flex flex-col gap-7">
      <header>
        <p className="page-eyebrow mb-1">Documents</p>
        <h2 className="font-display text-[1.7rem] sm:text-[2rem] leading-[1.15] tracking-tight">
          Mantras, sūktas, sādhanas, and study guides
        </h2>
        <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
          Each document gathers the introduction, the text in your script of
          choice, the recordings, and the related study materials in one
          place.
        </p>
      </header>

      {/* Filters: search + two dropdowns + clear */}
      <div className="flex flex-wrap items-end gap-3">
        <FilterDropdown
          label="Type"
          value={selectedType}
          options={typeTags}
          allLabel="All types"
          onChange={(v) => setTagParam({ type: v })}
        />
        <FilterDropdown
          label="Deity"
          value={selectedDeity}
          options={deityTags}
          allLabel="All deities"
          onChange={(v) => setTagParam({ deity: v })}
        />
        <div className="flex flex-col gap-1 flex-1 min-w-[200px] max-w-[420px]">
          <label className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
            Search
          </label>
          <input
            type="search"
            className="auth-input"
            placeholder="Title, subtitle, excerpt…"
            value={q}
            onChange={(e) => setSearch(e.target.value || null)}
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            className="chip chip-clear"
            onClick={() => {
              const sp = new URLSearchParams(params);
              sp.delete("tag");
              sp.delete("q");
              setParams(sp, { replace: true });
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Results */}
      {docsQ.isLoading ? (
        <p className="text-[var(--color-ink-mute)]">Loading…</p>
      ) : documents.length === 0 ? (
        <p className="veda-empty is-dashed font-grotesk text-[0.9rem] text-[var(--color-ink-mute)] italic">
          No documents match these filters yet.
        </p>
      ) : (
        <ul className="featured-grid">
          {documents.map((d) => (
            <li key={d.id}>
              <BrowseCard d={d} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterDropdown({
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  label: string;
  value: string;
  options: PublicDocumentTag[];
  allLabel: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1 min-w-[180px]">
      <label className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
        {label}
      </label>
      <select
        className="auth-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{allLabel}</option>
        {options.map((t) => (
          <option key={t.id} value={t.slug}>
            {t.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function BrowseCard({ d }: { d: PublicDocument }) {
  const typeTag = d.tags.find((t) => t.namespace === "type");
  return (
    <LibraryCard
      to={`/library/${d.slug}`}
      coverImage={d.coverImage}
      eyebrow={typeTag?.label ?? "Document"}
      title={d.title}
      subtitle={d.subtitle}
      excerpt={d.excerpt}
    />
  );
}
